import type { EditableComponent } from '@/components/edit/EditShell/EditShell';
import { UserRole } from '@/generated/graphql';

export const contentEditUserRoles: UserRole[] = Object.values(UserRole).filter(
    (role) => role.indexOf('content_') === 0,
);

type AllowedActions = {
    edit: boolean;
    publish: boolean;
    unpublish: boolean;
    canonicalNodeId: boolean;
    pathId: boolean;
    createChildren: boolean;
    removeChildren: boolean;
    sortChildren: boolean;
};

type AllowedAction = keyof AllowedActions;

type Options = {
    componentType: EditableComponent;
    nodeOwnerId?: number;
    nodePublishedLocked?: boolean;
    currentUserId: number;
    currentUserRoles: UserRole[];
};

export const getUsersCurrentEditPermission = (
    options: Options,
): AllowedAction[] => {
    const isOwner =
        options.nodeOwnerId == null || // @see UserCurrent.tsx as edit possibility.
        options.nodeOwnerId === options.currentUserId;
    const allowedActions: AllowedActions = {
        edit: false,
        publish: false,
        unpublish: false,
        canonicalNodeId: false,
        pathId: false,
        createChildren: false,
        removeChildren: false,
        sortChildren: false,
    };
    switch (options.componentType) {
        case 'REALM':
            if (
                options.currentUserRoles.includes(UserRole.ContentAdministrator)
            ) {
                allowedActions.edit = true;
                allowedActions.publish = true;
                allowedActions.removeChildren = true;
                allowedActions.pathId = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentMentor)) {
                allowedActions.sortChildren = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentManager)) {
                allowedActions.createChildren = true;
            }
            break;
        case 'ZONE':
            if (
                options.currentUserRoles.includes(UserRole.ContentAdministrator)
            ) {
                allowedActions.canonicalNodeId = !options.nodePublishedLocked;
                allowedActions.pathId = true;
                allowedActions.removeChildren = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentMentor)) {
                allowedActions.unpublish = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentManager)) {
                allowedActions.edit = true;
                allowedActions.publish = true;
                allowedActions.createChildren = true;
                allowedActions.sortChildren = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentAuthor)) {
                allowedActions.createChildren = true;
            }
            break;
        case 'MATRIX':
            if (
                options.currentUserRoles.includes(UserRole.ContentAdministrator)
            ) {
                allowedActions.canonicalNodeId = !options.nodePublishedLocked;
                allowedActions.pathId = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentMentor)) {
                allowedActions.unpublish = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentManager)) {
                allowedActions.edit = true;
                allowedActions.publish = true;
                allowedActions.createChildren = true;
                allowedActions.removeChildren = true;
                allowedActions.sortChildren = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentAuthor)) {
                allowedActions.edit = allowedActions.edit ? true : isOwner;
                allowedActions.publish = allowedActions.publish
                    ? true
                    : isOwner;
                allowedActions.createChildren = allowedActions.createChildren
                    ? true
                    : isOwner;
                allowedActions.removeChildren = allowedActions.removeChildren
                    ? true
                    : isOwner;
                allowedActions.sortChildren = allowedActions.sortChildren
                    ? true
                    : isOwner;
            }
            break;
        case 'CLUSTER':
            if (options.currentUserRoles.includes(UserRole.ContentManager)) {
                allowedActions.edit = true;
                allowedActions.publish = true;
                allowedActions.unpublish = true;
                allowedActions.createChildren = true;
                allowedActions.removeChildren = true;
                allowedActions.sortChildren = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentAuthor)) {
                allowedActions.edit = allowedActions.edit ? true : isOwner;
                allowedActions.publish = allowedActions.publish
                    ? true
                    : isOwner;
                allowedActions.unpublish = allowedActions.unpublish
                    ? true
                    : isOwner;
                allowedActions.createChildren = allowedActions.createChildren
                    ? true
                    : isOwner;
                allowedActions.removeChildren = allowedActions.removeChildren
                    ? true
                    : isOwner;
                allowedActions.sortChildren = allowedActions.sortChildren
                    ? true
                    : isOwner;
            }
            break;
        case 'PANEL':
            if (options.currentUserRoles.includes(UserRole.ContentManager)) {
                allowedActions.edit = true;
                allowedActions.publish = true;
                allowedActions.unpublish = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentAuthor)) {
                allowedActions.edit = allowedActions.edit ? true : isOwner;
                allowedActions.publish = allowedActions.publish
                    ? true
                    : isOwner;
                allowedActions.unpublish = allowedActions.unpublish
                    ? true
                    : isOwner;
            }
            break;
        case 'MEDIA':
            if (options.currentUserRoles.includes(UserRole.ContentManager)) {
                allowedActions.edit = true;
            }
            if (options.currentUserRoles.includes(UserRole.ContentAuthor)) {
                allowedActions.edit = allowedActions.edit ? true : isOwner;
            }
            break;
    }

    return (Object.keys(allowedActions) as AllowedAction[]).filter(
        (key) => allowedActions[key] === true,
    );
};

type CanEditPathIdOptions = {
    componentType: EditableComponent;
    published: boolean;
    currentUserRoles: UserRole[];
};

export const canEditPathId = (options: CanEditPathIdOptions) =>
    !(
        !options.currentUserRoles.includes(UserRole.ContentAdministrator) ||
        !['ZONE', 'MATRIX'].includes(options.componentType) ||
        options.published === true
    );
